/**
 * Copyright by:
 * ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */

import { Component, Inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface DialogData {
  title: string;
  header?: string;
  messageTitle?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  template?: TemplateRef<any>;
  templateHandlesLayout?: boolean;
  cancelStream?: Observable<void>;
}

/**
 * Shared dialog component that supports both message-based and template-based dialogs.
 * Provides customizable header, buttons, and content with optional event streaming.
 */
@Component({
  selector: 'app-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  standalone: false
})
export class DialogComponent implements OnInit, OnDestroy {
  // Public properties for template binding with sensible defaults
  header = '';
  messageTitle = '';
  message = '';
  confirmButtonText = 'OK';
  cancelButtonText = 'BACK';
  template?: TemplateRef<any>;
  templateHandlesLayout = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<DialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: DialogData
  ) {
    // Apply provided data onto the instance while preserving defaults
    Object.assign(this, {
      header: data.header ?? this.header,
      messageTitle: data.messageTitle ?? this.messageTitle,
      message: data.message ?? this.message,
      confirmButtonText: data.confirmButtonText ?? this.confirmButtonText,
      cancelButtonText: data.cancelButtonText ?? this.cancelButtonText,
      template: data.template,
      templateHandlesLayout: data.templateHandlesLayout ?? this.templateHandlesLayout
    });
  }

  ngOnInit(): void {
    // Subscribe to external cancel stream (if provided) and clean up with takeUntil
    this.data.cancelStream?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cancelClicked();
    });
  }

  ngOnDestroy(): void {
    // Signal teardown for any subscriptions using takeUntil
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirmClicked(): void {
    this.dialogRef.close('OK');
  }

  cancelClicked(): void {
    this.dialogRef.close('cancel');
  }
}
